/**
 * The dashboard frame for one route tree: Shell + Sidebar + TopBar + FiltersBar
 * (+ a page's floating dock), all derived from the matched route's registry
 * entry. Pages render in the Outlet.
 */
import { PageState, Shell } from '@deepdive-ui';
import { Suspense, useEffect, useMemo, useRef } from 'react';
import { Outlet, useLocation, useMatches } from 'react-router';

import { routeById, type AppRoute } from '@/app/router/registry';
import { useDataSource } from '@/features/data/DataSourceContext';
import { PageContext, type PageMeta } from '@/features/data/PageContext';
import { canOpen, openNavItems } from '@/features/shell/access';
import { FiltersBarContainer } from '@/features/shell/FiltersBarContainer';
import { PageDock } from '@/features/shell/docks';
import { ShellProviders } from '@/features/shell/ShellProviders';
import { SidebarContainer } from '@/features/shell/SidebarContainer';
import { TopBarContainer } from '@/features/shell/TopBarContainer';
import { useProfilePhotoUrl } from '@/features/settings/profilePhoto';
import { useAppDispatch } from '@/store/hooks';
import { openDialog } from '@/store/slices/uiSlice';

export type RouteHandle = { routeId: string };

function useCurrentRoute(): AppRoute | undefined {
  const matches = useMatches();
  for (let i = matches.length - 1; i >= 0; i--) {
    const handle = matches[i]?.handle as RouteHandle | undefined;
    if (handle?.routeId) return routeById.get(handle.routeId);
  }
  return undefined;
}

export function DashboardLayout({ visibleNavItems }: { visibleNavItems: ReadonlySet<string> }) {
  const route = useCurrentRoute();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const dispatch = useAppDispatch();
  const profilePhotoUrl = useProfilePhotoUrl();
  const { bootstrap } = useDataSource();
  const navItems = useMemo(
    () => openNavItems(visibleNavItems, bootstrap.modules),
    [visibleNavItems, bootstrap.modules],
  );
  // A module outside the person's access says so, instead of a page of failed cards.
  const allowed = canOpen(bootstrap.modules, route?.navItem);

  // The page scrolls inside <main>, not the window — reset it on navigation.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
  }, [location.pathname]);

  const page = useMemo<PageMeta>(
    () => ({ routeId: route?.id ?? '', filtersType: route?.filters ?? null }),
    [route],
  );

  return (
    <PageContext.Provider value={page}>
      <ShellProviders>
        <Shell
          mainRef={mainRef}
          label={route ? undefined : 'Not found'}
          sidebar={
            <SidebarContainer
              activeItemId={route?.navItem}
              visibleItemIds={navItems}
              profilePhotoUrl={profilePhotoUrl}
              onInviteMember={() => dispatch(openDialog('invite-member'))}
            />
          }
          header={route && <TopBarContainer key={route.id} route={route} />}
          filters={
            allowed && route?.filters ? <FiltersBarContainer type={route.filters} /> : undefined
          }
          dock={route?.dock ? <PageDock kind={route.dock} /> : undefined}
        >
          {allowed ? (
            <Suspense fallback={null}>
              <Outlet />
            </Suspense>
          ) : (
            <PageState
              title="You don’t have access to this module"
              description="Ask a workspace admin to give you access to it."
            />
          )}
        </Shell>
      </ShellProviders>
    </PageContext.Provider>
  );
}
